import React, { useState } from 'react';
import { ThumbsUp, Meh, ThumbsDown, X, Flame, Star, Instagram, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { heatAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const QuickRatingModal = ({ isOpen, onClose, staff, eventId, eventName, onRated, onSkip }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [note, setNote] = useState('');

  if (!staff) return null;

  const ratings = [
    { 
      key: 'great', 
      label: 'Great', 
      icon: ThumbsUp, 
      color: 'text-green-400 hover:bg-green-500/20 border-green-500/30',
      activeColor: 'bg-green-500/20 border-green-500 text-green-400',
      stars: 5,
      heat: ''
    },
    { 
      key: 'good', 
      label: 'Good', 
      icon: Meh, 
      color: 'text-yellow-400 hover:bg-yellow-500/20 border-yellow-500/30',
      activeColor: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
      stars: 4,
      heat: ''
    },
    { 
      key: 'okay', 
      label: 'Needs Work', 
      icon: ThumbsDown, 
      color: 'text-gray-400 hover:bg-gray-500/20 border-gray-500/30',
      activeColor: 'bg-gray-500/20 border-gray-500 text-gray-400',
      stars: 3,
      heat: ''
    },
  ];

  const handleRating = async () => {
    if (!selectedRating) return;
    
    setLoading(true);
    try {
      const result = await heatAPI.quickRate(staff.id || staff.staffId, eventId, selectedRating, note);
      
      toast({
        title: 'Rating Submitted!',
        description: `${staff.name || staff.staffName} received ${result.stars} stars and ${result.heatBonus} heat points`,
      });
      
      setSelectedRating(null);
      setNote('');
      
      if (onRated) onRated(result);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit rating',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialTag = async () => {
    setLoading(true);
    try {
      await heatAPI.awardSocialTag(staff.id || staff.staffId, eventId, 'instagram');
      
      toast({
        title: 'Social Tag Bonus!',
        description: `+10 heat points awarded to ${staff.name || staff.staffName}`,
      });
    } catch (error) {
      toast({
        title: 'Already Claimed',
        description: error.response?.data?.detail || 'Social tag bonus already claimed today',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await heatAPI.dismissRating(staff.id || staff.staffId, eventId);
    } catch (error) {
      console.debug('Dismiss rating error:', error);
    } finally {
      setLoading(false);
    }
    
    setSelectedRating(null);
    setNote('');
    
    if (onSkip) {
      onSkip();
    } else {
      onClose();
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      handleSkip();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md p-0 bg-[#0B0F1A] border border-[#D4AF37]/15 rounded-3xl overflow-hidden"
        style={{ boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(212,175,55,0.06)' }}
        data-testid="quick-rating-modal"
      >
        {/* Header — single close handled by the Dialog's built-in button */}
        <div className="relative px-6 pt-6 pb-5">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
          <div className="flex items-center gap-3.5">
            <div
              className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E5C76B] to-[#D4AF37] flex items-center justify-center text-[#0B0F1A] font-bold text-lg shrink-0"
              style={{ boxShadow: '0 0 0 2px rgba(212,175,55,0.25), 0 8px 24px -6px rgba(212,175,55,0.45)' }}
            >
              {(staff.name || staff.staffName)?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div className="min-w-0 pr-8">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#D4AF37]/70 uppercase mb-0.5">Performance Rating</p>
              <h3 className="text-white font-semibold text-lg leading-tight truncate">{staff.name || staff.staffName}</h3>
              <p className="text-gray-400 text-xs truncate">{eventName || 'Quick Rating'}</p>
            </div>
          </div>
        </div>

        {/* Rating Options */}
        <div className="px-6 pb-2">
          <p className="text-gray-300 text-center text-sm mb-5">
            How did <span className="text-white font-medium">{staff.name || staff.staffName}</span> do?
          </p>

          <div className="grid grid-cols-3 gap-3 mb-5">
            {ratings.map((rating) => {
              const Icon = rating.icon;
              const isSelected = selectedRating === rating.key;
              const accent = { great: '#34D399', good: '#D4AF37', okay: '#94A3B8' }[rating.key];
              return (
                <button
                  key={rating.key}
                  onClick={() => setSelectedRating(rating.key)}
                  className="relative p-4 rounded-2xl border transition-all duration-200 flex flex-col items-center hover:-translate-y-0.5"
                  style={{
                    borderColor: isSelected ? accent : 'rgba(255,255,255,0.08)',
                    background: isSelected ? `${accent}1f` : 'rgba(255,255,255,0.02)',
                    boxShadow: isSelected ? `0 0 0 1px ${accent}, 0 8px 24px -10px ${accent}80` : 'none',
                  }}
                  data-testid={`rating-${rating.key}`}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-2 transition-colors"
                    style={{ background: isSelected ? `${accent}26` : 'rgba(255,255,255,0.04)' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: isSelected ? accent : 'rgba(255,255,255,0.5)' }} />
                  </div>
                  <p className="text-[13px] font-medium" style={{ color: isSelected ? accent : 'rgba(255,255,255,0.85)' }}>{rating.label}</p>
                  <div
                    className="flex items-center justify-center gap-1 mt-1.5 px-2 py-0.5 rounded-full"
                    style={{ background: isSelected ? `${accent}1a` : 'rgba(255,255,255,0.04)' }}
                  >
                    <Star className="w-3 h-3" style={{ color: isSelected ? accent : 'rgba(255,255,255,0.4)', fill: isSelected ? accent : 'none' }} />
                    <span className="text-[11px] font-semibold" style={{ color: isSelected ? accent : 'rgba(255,255,255,0.5)' }}>{rating.stars}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Optional Note */}
          {selectedRating && (
            <div className="mb-4 animate-in fade-in slide-in-from-top-1 duration-200">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (optional)…"
                className="w-full p-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm resize-none focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                rows={2}
                data-testid="rating-note"
              />
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleRating}
            disabled={!selectedRating || loading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-[#E5C76B] to-[#D4AF37] text-[#0B0F1A] font-semibold hover:opacity-95 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-40 disabled:hover:translate-y-0 shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
            data-testid="submit-rating-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Rating'}
          </Button>

          {/* Skip Option */}
          <button
            onClick={handleSkip}
            className="w-full mt-3 text-gray-500 hover:text-gray-300 text-sm transition-colors"
            data-testid="skip-rating-btn"
          >
            Skip for now
          </button>
        </div>

        {/* Heat Points Info */}
        <div className="px-6 pb-6 pt-1">
          <div className="flex items-start gap-2 px-3.5 py-3 rounded-xl border border-[#D4AF37]/[0.12] bg-[#D4AF37]/[0.04]">
            <Flame className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Heat points are auto-awarded for completed events, GPS check-ins &amp; on-time arrivals.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickRatingModal;
